import {
  Attributes,
  createElement,
  CSSProperties,
  Fragment,
  FunctionComponent,
  useMemo,
} from 'react';
import {
  ComponentFactory,
  ComponentRendering,
  Field,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import {
  ComponentProps,
  isColorBright,
  isNotNil,
  resolveColorProperty,
} from '@gn-audio/jss-components';

import { executeGQLQuery } from 'api/graphqlApi';
import { Eyebrow } from 'common/components/Eyebrow';
import { FirstButtonSettings, SecondButtonSettings } from 'common/types/buttonTemplateTypes';
import { PlaceholderContainerContext } from 'components/Product/KSP/Placeholder/PlaceholderContainerContext';
import { DualButtonBlock } from 'composables/shared/DualButtonBlock/DualButtonBlock';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

type GenericKSPSpotLayout = 'vertical' | 'horizontal';

type PlaceholderDatasource = {
  name: string;
  id: string;
  displayName: string;
  fields: Record<string, Field>;
};
type GenericKSPSpotFields = {
  backgroundColor: Field<string>;
  placeholder: Array<PlaceholderDatasource>;
  layoutOrientation: Field<GenericKSPSpotLayout>;
  dataSource: GenericKSPSpot;
  text: Field<string>;
  header: Field<string>;
  title: Field<string>;
  trackingSpotName?: Field<string>;
  firstCtaButtonTrackingName?: Field<string>;
  secondCtaButtonTrackingName?: Field<string>;
} & FirstButtonSettings &
  SecondButtonSettings;

type PlaceholderTemplate = {
  item: {
    id: string;
    name: string;
    template: {
      name: string;
    };
  };
};
type GenericKSPSpotProps = {
  fields: GenericKSPSpotFields;
  parentHeader?: string;
  parentTitle?: string;
  parentText?: string;
  placeholderTemplates: PlaceholderTemplate[];
  componentFactory: ComponentFactory;
} & ComponentProps;

type GenericKSPSpot = {
  Header: Field<string>;
  Title: Field<string>;
  Text: Field<string>;
};

const KSP = ({
  fields,
  parentHeader,
  parentTitle,
  parentText,
  placeholderTemplates,
  componentFactory,
}: GenericKSPSpotProps) => {
  const {
    firstBtnText,
    firstBtnLink,
    firstBtnIcon,
    firstBtnVariant,
    firstBtnSize,
    secondBtnLink,
    secondBtnVariant,
    secondBtnText,
    secondBtnIcon,
    secondBtnSize,
    trackingSpotName,
    firstCtaButtonTrackingName,
    secondCtaButtonTrackingName,
  } = fields;

  const spotRef = useSpotTracking(trackingSpotName?.value);
  const kspFields = fields.dataSource;
  const header = parentHeader || kspFields?.Header?.value || '';
  const title = parentTitle || kspFields?.Title?.value || '';
  const text = parentText || kspFields?.Text?.value || '';

  const layoutOrientation = fields.layoutOrientation?.value || 'vertical';
  const backgroundColorHex = resolveColorProperty(fields.backgroundColor?.value ?? '#ffffff');
  const backgroundTextColorClass = isColorBright(backgroundColorHex) ? 'light' : 'dark';

  const backgroundStyle = {
    backgroundColor: backgroundColorHex,
  } as CSSProperties;

  const paddingOverwrites = {
    '--top-padding-mobile': 0,
    '--top-padding-tablet': 0,
    '--top-padding-desktop': 0,
    '--bottom-padding-mobile': 0,
    '--bottom-padding-tablet': 0,
    '--bottom-padding-desktop': 0,
  } as CSSProperties;

  const components = placeholderTemplates
    ?.map((template, index) => {
      const dataSource = fields.placeholder?.[index];
      const placeholderTemplate = template.item.template;
      if (!dataSource || !placeholderTemplate) {
        return;
      }
      const component = componentFactory(placeholderTemplate.name);
      if (!component) {
        return;
      }
      return {
        component,
        id: dataSource.id,
        props: {
          fields: dataSource,
          rendering: { fields: dataSource, dataSource: dataSource.id },
        } as Attributes,
      };
    })
    .filter(isNotNil);

  const context = useMemo(() => {
    return { isInsidePlaceholder: true };
  }, []);
  return (
    <div
      ref={spotRef}
      data-wt-spot={trackingSpotName?.value}
      style={backgroundStyle}
      className={classNames(
        'container-fluid flex flex-col gap-2 md:gap-6',
        backgroundTextColorClass
      )}
    >
      {layoutOrientation === 'horizontal' && (
        <div className="container max-w-6xl space-y-6">
          {title && <Eyebrow text={title} />}
          <div className="grid gap-5 md:grid-cols-2 md:gap-6">
            {header && (
              <h2
                className="heading-2 font-bold"
                dangerouslySetInnerHTML={{ __html: sanitize(header) }}
              />
            )}
            <div className="lg:pl-[60px]">
              {text && (
                <div
                  className="paragraph-large flex flex-col gap-2"
                  dangerouslySetInnerHTML={{ __html: sanitize(text) }}
                />
              )}
              <DualButtonBlock
                firstBtnText={firstBtnText}
                firstBtnLink={firstBtnLink}
                firstBtnIcon={firstBtnIcon}
                firstBtnVariant={firstBtnVariant}
                firstBtnSize={firstBtnSize}
                firstCtaButtonTrackingName={firstCtaButtonTrackingName}
                secondBtnText={secondBtnText}
                secondBtnLink={secondBtnLink}
                secondBtnIcon={secondBtnIcon}
                secondBtnVariant={secondBtnVariant}
                secondBtnSize={secondBtnSize}
                secondCtaButtonTrackingName={secondCtaButtonTrackingName}
                className="mt-10"
              />
            </div>
          </div>
        </div>
      )}
      {layoutOrientation === 'vertical' && (
        <div className="container max-w-6xl py-0 text-center">
          <div className="flex flex-col gap-4 md:gap-6">
            {title && <Eyebrow text={title} align="center" />}
            {header && (
              <h2 className="heading-2" dangerouslySetInnerHTML={{ __html: sanitize(header) }} />
            )}
            {text && (
              <div
                className="paragraph-large flex flex-col gap-2"
                dangerouslySetInnerHTML={{ __html: sanitize(text) }}
              ></div>
            )}
            <DualButtonBlock
              firstBtnText={firstBtnText}
              firstBtnLink={firstBtnLink}
              firstBtnIcon={firstBtnIcon}
              firstBtnVariant={firstBtnVariant}
              firstBtnSize={firstBtnSize}
              firstCtaButtonTrackingName={firstCtaButtonTrackingName}
              secondBtnText={secondBtnText}
              secondBtnLink={secondBtnLink}
              secondBtnIcon={secondBtnIcon}
              secondBtnVariant={secondBtnVariant}
              secondBtnSize={secondBtnSize}
              secondCtaButtonTrackingName={secondCtaButtonTrackingName}
              className="mt-4 justify-center"
            />
          </div>
        </div>
      )}
      {!!components?.length && (
        <div
          className="placeholder-container flex flex-col gap-8 overflow-hidden md:gap-12"
          style={paddingOverwrites}
        >
          <PlaceholderContainerContext.Provider value={context}>
            {components.map(({ component, id, props }) => {
              return (
                <Fragment key={id}>{createElement(component as FunctionComponent, props)}</Fragment>
              );
            })}
          </PlaceholderContainerContext.Provider>
        </div>
      )}
    </div>
  );
};

const query = `query GetItemTemplate($path: String!, $language: String!) {
  item(path: $path, language: $language) {
    id
    name
    template {
      name
    }
  }
}`;

export const getStaticProps = async (rendering: ComponentRendering<GenericKSPSpotFields>) => {
  const fields = rendering.fields as GenericKSPSpotFields;

  const parentHeader = fields.header?.value || '';
  const parentTitle = fields.title?.value || '';
  const parentText = fields.text?.value || '';

  const placeholder = fields?.placeholder || [];
  const placeholderIds = placeholder?.map((item) => {
    return item.id;
  });

  const placeholderTemplates = await Promise.all(
    placeholderIds.map((id) => {
      return executeGQLQuery(query, {
        path: id,
        language: 'en',
      });
    })
  );

  return { parentHeader, parentTitle, parentText, placeholderTemplates };
};

export default withDatasourceCheck()(KSP);
